// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { localize } from '@ta/common.i18n';
import { Link } from '@ta/common.routing';
import classNames from '@ta/common.classnames';
import { getTripUsersForDisplay, getTripUsers } from '@ta/trips.trip-util';
import * as Types from '@ta/trips.trip-types';

import styles from './TripByLine.less';

type Props = {|
  /** The trip to display */
  trip: Types.Trip,
  /** A css class to apply to the user link */
  linkClassName: string,
  /** The maximum number of names to show in the byline */
  maxNames?: number,
  /** Callback when the 'and more' cta is clicked */
  onExpand?: ?() => void,
  /** Set to true to use small size */
  isSmall?: boolean,
|};

type InternalProps = {|
  ...Props,
  /** Is the component being displaying in an RTL language. */
  isRtl: boolean,
  viewportCategory: string,
|};

const defaultProps = {
  isRtl: false,
  maxNames: 2,
  onExpand: null,
  isSmall: false,
};

const DT_MAX_CHARS = 100;
const MW_MAX_CHARS = 70;
const MAX_CHARS_SMALL = 65;

function getCharacterLimit(viewportCategory, isSmall) {
  if (isSmall) {
    return MAX_CHARS_SMALL;
  }
  if (viewportCategory === 'MOBILE') {
    return MW_MAX_CHARS;
  }
  return DT_MAX_CHARS;
}

type UserLinkProps = {|
  className: string,
  member: Types.Member,
|};

const UserLink = ({ className, member }: UserLinkProps) => (
  <Link
    className={classNames(className, { [styles.verified]: member.isVerified })}
    href={member?.route?.url}
    onClick={e => e.stopPropagation()}
  >
    {member.displayName}
  </Link>
);

/**
 * Displays the 'By {username1} {username2}' line.
 */
const TripByLine = (props: InternalProps) => {
  const mapToUserLink = (member: ?Types.Member, index: number, array: any) => {
    const isLast = index === array.length - 1;

    return (
      member && (
        <React.Fragment key={member.id || member.username}>
          <UserLink className={props.linkClassName} member={member} />
          {!isLast && ', '}
        </React.Fragment>
      )
    );
  };

  const readMoreText = localize('trips_byline_v2'); // By
  const andMoreText = localize('trips_and_more'); // and more
  const translationTotal = readMoreText.length + andMoreText.length;
  const characterLimit = getCharacterLimit(props.viewportCategory, props.isSmall);

  const tripUsers = getTripUsers(props.trip);
  const byLineUsers = getTripUsersForDisplay(
    props.trip,
    props.maxNames || defaultProps.maxNames,
    characterLimit - translationTotal,
    props.isRtl,
  );
  const ctaIsClickable = !!props.onExpand;
  const clickableStyles = `${props.linkClassName} ${styles.clickable}`;

  const onClick = e => {
    if (props.onExpand) {
      props.onExpand();
      e.stopPropagation();
    }
  };

  const wasTruncated = byLineUsers.length < tripUsers.length;
  const andMore = wasTruncated ? (
    <span
      onClick={onClick}
      className={classNames(styles.andMoreCTA, { [clickableStyles]: ctaIsClickable })}
    >
      {andMoreText}
    </span>
  ) : null;

  return (
    <div>
      {readMoreText} {byLineUsers.map(mapToUserLink)} {andMore}
    </div>
  );
};

TripByLine.defaultProps = defaultProps;

export default connect<InternalProps, Props, _, _, _, _>(state => ({
  isRtl: state.i18n.isRtl,
  viewportCategory: state.meta.device.viewportCategory,
}))(TripByLine);
